import { createContext, useContext } from 'react'
import type { ScribeSession } from '@/hooks/useScribeSession'

/**
 * Everything about the pop-out that is NOT a component.
 *
 * Split out so `ScribePopout.tsx` and `ScribePopoutHost.tsx` each export a
 * component and nothing else — Fast Refresh gives up on a module that mixes
 * the two, which would mean full reloads mid-consult while working on this.
 */

/** Window sizes in CSS px. The host resizes to these as the phase changes. */
export const COMPACT_SIZE = { width: 240, height: 96 }
export const EXPANDED_SIZE = { width: 460, height: 640 }

/** Whether this phase wants the big window. */
export function isExpandedPhase(phase: ScribeSession['phase']): boolean {
  return phase === 'ready' || phase === 'failed'
}

export interface ScribePopoutApi {
  isOpen: boolean
  /**
   * True document Picture-in-Picture (always-on-top). False means the popup
   * fallback, which cannot float above the EMR — worth saying out loud rather
   * than letting a clinician wonder why the window keeps disappearing.
   */
  alwaysOnTop: boolean
  /**
   * MUST be called straight from a click handler — it needs user activation.
   *
   * `noteId` is the note the window is being opened ON. Pass it and the window
   * shows that note; omit it and the window opens as a bare scribe waiting for
   * a recording. A live recording always takes precedence over either.
   */
  open: (noteId?: number | null) => void
  close: () => void
}

export const ScribePopoutContext = createContext<ScribePopoutApi | null>(null)

/**
 * Returns null outside the provider rather than throwing: a trigger rendered
 * in a test harness or a route that never mounts the shell should render
 * nothing, not crash the page it is decorating.
 */
export function useScribePopout(): ScribePopoutApi | null {
  return useContext(ScribePopoutContext)
}
